import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import type {PDFDocumentLoadingTask, PDFDocumentProxy} from 'pdfjs-dist';

// pdfjs-dist is lazily imported (like jspdf elsewhere) so it only ships in a
// lazy chunk and only loads the first time a PDF is actually viewed.
let pdfjsModulePromise: Promise<typeof import('pdfjs-dist')> | null = null;

function loadPdfjs(): Promise<typeof import('pdfjs-dist')> {
    if (!pdfjsModulePromise) {
        pdfjsModulePromise = import('pdfjs-dist').then((pdfjs) => {
            pdfjs.GlobalWorkerOptions.workerSrc = 'assets/pdfjs/pdf.worker.min.mjs';
            return pdfjs;
        });
    }
    return pdfjsModulePromise;
}

/**
 * Renders a PDF inline by drawing its pages to canvases with PDF.js.
 *
 * Replaces the previous native <object type="application/pdf"> embed, which
 * handed untrusted uploads to the browser's own PDF plugin where embedded
 * PDF JavaScript and actions execute (pen test, Aug 2026). PDF.js never
 * executes PDF-embedded JavaScript or actions unless a scripting manager is
 * wired up, which this component deliberately never does. It also works in
 * Safari, which the old embed did not.
 *
 * The bytes are fetched with fetch/XHR, so the presigned URL's
 * Content-Disposition: attachment does not affect inline viewing.
 */
@Component({
    selector: 'app-pdf-viewer',
    templateUrl: './pdf-viewer.component.html',
    styleUrl: './pdf-viewer.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PdfViewerComponent implements OnChanges, OnDestroy {
    @Input() src: string | null = null;
    @Input() title: string = '';
    // Small mode renders just the first page as a non-interactive tile
    // preview; clicking/activating it emits (open) so the host can launch the
    // full viewer (mirrors the image/video tile behaviour).
    @Input() small: boolean = false;
    @Output() open: EventEmitter<void> = new EventEmitter();
    // Emitted once when loading fails and a refreshed presigned URL might fix
    // it; the host should refresh [src]. Further failures emit (loadFailed).
    @Output() urlExpired: EventEmitter<void> = new EventEmitter();
    @Output() loadFailed: EventEmitter<void> = new EventEmitter();

    @ViewChild('pagesHost', {static: true}) pagesHost!: ElementRef<HTMLDivElement>;

    loading: boolean = false;
    private loadingTask: PDFDocumentLoadingTask | null = null;
    private renderToken: number = 0;
    private refreshRequested: boolean = false;

    // PDF.js resolves its promises from web-worker callbacks, outside
    // Angular's zone — state updates after those awaits must re-enter the
    // zone or the view never refreshes (spinners hang on quiet pages).
    constructor(private zone: NgZone) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['src']) {
            this.loadDocument().catch((e): void => {
                console.error(e);
            });
        }
    }

    ngOnDestroy(): void {
        this.renderToken++;
        this.destroyDocument();
    }

    onHostClick(): void {
        if (this.small) {
            this.open.emit();
        }
    }

    onHostSpace(event: Event): void {
        if (this.small) {
            event.preventDefault();
            this.open.emit();
        }
    }

    private destroyDocument(): void {
        if (this.loadingTask) {
            this.loadingTask.destroy().catch((): void => undefined);
            this.loadingTask = null;
        }
    }

    private async loadDocument(): Promise<void> {
        const src: string | null = this.src;
        const token: number = ++this.renderToken;
        this.destroyDocument();
        this.pagesHost.nativeElement.replaceChildren();
        if (!src) {
            return;
        }
        this.loading = true;
        try {
            const pdfjs = await loadPdfjs();
            const loadingTask: PDFDocumentLoadingTask = pdfjs.getDocument({
                url: src,
                cMapUrl: 'assets/pdfjs/cmaps/',
                cMapPacked: true,
                standardFontDataUrl: 'assets/pdfjs/standard_fonts/',
                wasmUrl: 'assets/pdfjs/wasm/',
                iccUrl: 'assets/pdfjs/iccs/',
            });
            const pdfDocument: PDFDocumentProxy = await loadingTask.promise;
            if (token !== this.renderToken) {
                loadingTask.destroy().catch((): void => undefined);
                return;
            }
            this.loadingTask = loadingTask;
            await this.renderPages(pdfDocument, token);
            if (token === this.renderToken) {
                this.zone.run((): void => {
                    this.loading = false;
                    this.refreshRequested = false;
                });
            }
        } catch (error) {
            if (token !== this.renderToken) {
                return;
            }
            console.error(error);
            this.zone.run((): void => {
                this.loading = false;
                if (!this.refreshRequested && this.urlExpired.observed) {
                    // The presigned URL may simply have expired; ask the host
                    // to refresh it once before giving up.
                    this.refreshRequested = true;
                    this.urlExpired.emit();
                } else {
                    this.loadFailed.emit();
                }
            });
        }
    }

    private async renderPages(pdfDocument: PDFDocumentProxy, token: number): Promise<void> {
        const host: HTMLDivElement = this.pagesHost.nativeElement;
        // Let layout settle (dialog open animation etc.) before measuring the
        // container, or the fit calculation below sees a half-laid-out box.
        await new Promise<void>((resolve): void => {
            requestAnimationFrame((): void => resolve());
        });
        if (token !== this.renderToken) {
            return;
        }
        const hostWidth: number = host.clientWidth || host.getBoundingClientRect().width || 600;
        const hostHeight: number = host.clientHeight || host.getBoundingClientRect().height || hostWidth;
        const pageCount: number = this.small ? 1 : pdfDocument.numPages;
        // Render at device resolution (capped) so text stays crisp on retina
        // screens while the canvas is displayed at CSS size.
        const outputScale: number = Math.min(window.devicePixelRatio || 1, 2);

        for (let pageNumber: number = 1; pageNumber <= pageCount; pageNumber++) {
            if (token !== this.renderToken) {
                return;
            }
            const page = await pdfDocument.getPage(pageNumber);
            const baseViewport = page.getViewport({scale: 1});
            // Full view fits the whole page inside the viewer (matching the
            // old embed's fit-to-page default); small tiles render at tile
            // width and are letterboxed by CSS object-fit: contain.
            const scale: number = this.small
                ? hostWidth / baseViewport.width
                : Math.min(hostWidth / baseViewport.width, hostHeight / baseViewport.height);
            const viewport = page.getViewport({scale});

            const canvas: HTMLCanvasElement = document.createElement('canvas');
            canvas.className = 'pdfPage';
            canvas.width = Math.floor(viewport.width * outputScale);
            canvas.height = Math.floor(viewport.height * outputScale);
            if (!this.small) {
                canvas.style.width = `${Math.floor(viewport.width)}px`;
                canvas.style.height = `${Math.floor(viewport.height)}px`;
            }
            canvas.setAttribute('role', 'img');
            canvas.setAttribute('aria-label', `${this.title} - page ${pageNumber} of ${pdfDocument.numPages}`);
            host.appendChild(canvas);

            await page.render({
                canvas,
                viewport,
                transform: outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : undefined,
            }).promise;

            // The document is readable as soon as the first page is up; any
            // remaining pages keep rendering behind the scenes.
            if (pageNumber === 1 && token === this.renderToken) {
                this.zone.run((): void => {
                    this.loading = false;
                });
            }
        }
    }
}
